import React, { useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Box, Typography, Button } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { AddCircle as AddCircleIcon } from '@mui/icons-material'
import { randomId } from '@mui/x-data-grid-generator'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import BasicDataGrid from 'components/generic/datagrid'
import { isDefaultRow } from 'components/containers/settings/utils/helperFunctions'
import { dispatchSnackbar } from 'utils/helpers'

const useStyles = makeStyles(theme => ({
  root: {
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    '& .Mui-error': {
      color: theme.palette.error.main,
    },
  },
}))

const SettingsGrid = ({
  title,
  subTitle,
  subTitle2,
  rowType,
  generateColumns,
  path,
  dataGridProps,
  setHasEditedGrid,
  apiRefDict,
  setApiRefDict,
}) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const apiRef = useGridApiRef()
  const methods = useFormContext()
  const fields = methods.getValues(path)
  const [editRowsModel, setEditRowsModel] = useState({})

  const onUpdateGrid = useCallback(() => {
    setHasEditedGrid(true)
    const newRows = Array.from(apiRef.current.getRowModels().values())
    methods.setValue(path, newRows, { shouldDirty: true })
  }, [apiRef])

  useEffect(() => {
    if (apiRef?.current?.subscribeEvent) {
      apiRef.current.subscribeEvent('rowsUpdate', () => {
        onUpdateGrid()
      })
    }
    if (!apiRefDict[path]) {
      setApiRefDict(prev => ({
        ...prev,
        [path]: apiRef,
      }))
    }
  }, [apiRef])

  const onDeleteRow = useCallback(
    rowId => {
      apiRef.current?.updateRows([{ id: rowId, _action: 'delete' }])
      onUpdateGrid()
      if (editRowsModel[rowId]) {
        const newEditRowsModel = { ...editRowsModel }
        delete newEditRowsModel[rowId]
        setEditRowsModel(newEditRowsModel)
      }
    },
    [apiRef, fields],
  )

  const columns = generateColumns({ apiRef, onDeleteRow })

  const onClickNewRow = useCallback(() => {
    const id = randomId()
    apiRef.current.updateRows([{ id, isNew: true, active: true, isActive: true }])
    // Wait for the grid to render with the new row
    setTimeout(() => {
      apiRef.current.scrollToIndexes({
        rowIndex: apiRef.current.getRowsCount() - 1,
      })

      if (columns.length > 0) {
        apiRef.current.setCellFocus(id, columns[0].field)
      }
    }, 150)
  }, [apiRef])

  const preventDuplicateNames = useCallback(
    newModel => {
      const updatedModel = { ...newModel }
      if (rowType === 'Supplier Type' || rowType === 'Vendor Class') {
        Object.keys(updatedModel).forEach(id => {
          const { value } = updatedModel[id]?.name || {}
          const otherRows = Array.from(apiRef.current.getRowModels().values()).filter(row => row.id !== id)
          const nameAlreadyTaken = otherRows.some(row => !!value && row?.name === value)
          updatedModel[id].name = {
            ...updatedModel[id].name,
            error: nameAlreadyTaken,
          }
          if (nameAlreadyTaken) {
            dispatchSnackbar(dispatch, 'error', 'This name is already taken')
          }
        })
      }
      setEditRowsModel(updatedModel)
    },
    [apiRef],
  )

  return (
    <>
      <Typography variant="h6" color="textPrimary">
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subTitle}
      </Typography>
      {subTitle2 && (
        <Typography variant="body2" color="textSecondary">
          {subTitle2}
        </Typography>
      )}
      <Box mt={1}>
        <Button startIcon={<AddCircleIcon />} onClick={onClickNewRow}>
          {`Add ${rowType}`}
        </Button>
      </Box>
      <Box mt={1}>
        <BasicDataGrid
          className={classes.root}
          density="compact"
          height="auto"
          rows={fields}
          columns={columns}
          apiRef={apiRef}
          editMode="cell"
          disableColumnMenu
          hideFooter
          isCellEditable={params => !isDefaultRow(params)}
          editRowsModel={editRowsModel}
          onEditRowsModelChange={preventDuplicateNames}
          onCellClick={(_, event) => {
            event.stopPropagation()

            Object.values(apiRefDict).forEach(ref => {
              ref.current.state.focus.cell = null
            })
          }}
          {...dataGridProps}
        />
      </Box>
    </>
  )
}

SettingsGrid.propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  subTitle2: PropTypes.string,
  rowType: PropTypes.string.isRequired,
  generateColumns: PropTypes.func,
  path: PropTypes.string.isRequired,
  dataGridProps: PropTypes.object,
  fields: PropTypes.array,
  setHasEditedGrid: PropTypes.func,
  apiRefDict: PropTypes.object,
  setApiRefDict: PropTypes.func,
}

SettingsGrid.defaultProps = {
  subTitle2: '',
  generateColumns: () => [],
  fields: [],
  dataGridProps: {},
  setHasEditedGrid: () => {},
  apiRefDict: {},
  setApiRefDict: () => {},
}

export default SettingsGrid
